import { Link } from "react-router-dom";
import { AlertOctagon, AlertTriangle, Info, ChevronRight, ArrowRight } from "lucide-react";

/**
 * NeedsAttention — the single most operationally useful panel.
 *
 * Ranks items by (spec):
 *   1. Vehicle delivery overdue
 *   2. Jobs blocked by parts
 *   3. Estimates awaiting approval
 *   4. Ready vehicles with pending payment
 *   5. Pickups / drops due soon
 *   6. Low-stock item linked to an active job
 * Displays only the top 5 highest priority items.
 */
export function NeedsAttention({ items = [], onViewAll }) {
  const top = items.slice(0, 5);
  const sevMap = {
    urgent: { Icon: AlertOctagon, chip: "bg-[#e63946]/10 text-[#e63946]", border: "border-l-[#e63946]" },
    attention: { Icon: AlertTriangle, chip: "bg-[#F79009]/10 text-[#F79009]", border: "border-l-[#F79009]" },
    normal: { Icon: Info, chip: "bg-[#457b9d]/10 text-[#457b9d]", border: "border-l-[#457b9d]" },
  };

  return (
    <section
      className="bg-white rounded-xl border border-[#E4E7EC]"
      data-testid="needs-attention"
    >
      <header className="px-5 py-3.5 border-b border-[#F1FAEE] flex items-center justify-between">
        <div>
          <h3 className="text-[15px] font-semibold text-[#1d3557]">Needs Attention</h3>
          <p className="text-[11px] text-[#667085] mt-0.5">
            {top.length === 0 ? "You are all caught up." : `Top ${top.length} exception${top.length > 1 ? "s" : ""} across the workshop.`}
          </p>
        </div>
        {items.length > 5 && (
          <button
            type="button"
            onClick={onViewAll}
            data-testid="needs-attention-view-all"
            className="text-[12px] font-semibold text-[#457b9d] hover:text-[#e63946] inline-flex items-center gap-1"
          >
            View all actions <ArrowRight size={13} />
          </button>
        )}
      </header>
      {top.length === 0 ? (
        <div className="p-10 text-center">
          <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-[#F1FAEE] grid place-items-center text-[#12B76A]">
            <Info size={18} />
          </div>
          <div className="text-[13px] font-semibold text-[#1d3557]">All clear</div>
          <p className="text-[12px] text-[#667085] mt-1">
            No urgent issues right now. Great work.
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-[#F1FAEE]">
          {top.map((it, i) => {
            const meta = sevMap[it.severity] || sevMap.normal;
            const { Icon } = meta;
            return (
              <li
                key={i}
                className={`flex items-center gap-3 px-5 py-3.5 border-l-2 ${meta.border} hover:bg-[#F7F9FC]`}
                data-testid={`needs-attention-item-${i}`}
              >
                <div className={`w-9 h-9 rounded-md grid place-items-center shrink-0 ${meta.chip}`}>
                  <Icon size={16} strokeWidth={2.4} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13.5px] font-semibold text-[#1d3557] truncate">
                    {it.title}
                  </div>
                  <div className="text-[11.5px] text-[#667085] truncate">
                    {it.detail}
                  </div>
                </div>
                {it.url && (
                  <Link
                    to={it.url}
                    data-testid={`needs-attention-cta-${i}`}
                    className="hidden md:inline-flex items-center gap-1 h-8 px-3 rounded-md border border-[#E4E7EC] text-[12px] font-semibold text-[#1d3557] hover:bg-[#F1FAEE]"
                  >
                    {it.cta_label || "View"}
                    <ChevronRight size={12} />
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
